import { SEVERITIES, type TraceSummary } from "@ti/core";
import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarRange, ListTree, Loader2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import { PageHeader } from "@/components/domain/misc";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Select } from "@/components/ui/input";
import { QueryBar } from "@/features/traces/query-bar";
import { traceColumns } from "@/features/traces/trace-columns";
import { BUCKET, fmtBucket, type TraceFilters, VolumeHistogram } from "@/features/traces/volume-histogram";
import { q } from "@/lib/api";
import { fmtDateTime, fmtInt } from "@/lib/format";

const OUTCOMES = ["success", "failure", "escalated", "abandoned"] as const;
const PAGE_SIZE = 50;

const searchSchema = z.object({
  q: z.string().optional().catch(undefined),
  agentId: z.string().optional().catch(undefined),
  outcome: z.enum(OUTCOMES).optional().catch(undefined),
  severity: z.enum(SEVERITIES).optional().catch(undefined),
  signal: z.string().optional().catch(undefined),
  from: z.iso.datetime().optional().catch(undefined),
  to: z.iso.datetime().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/traces/")({
  validateSearch: searchSchema,
  component: TracesPage,
});

const clean = (v: string | undefined) => (v && v.trim() ? v.trim() : undefined);

function TracesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const agents = useQuery(q.agents());

  const filters: TraceFilters = useMemo(
    () => ({ q: search.q, agentId: search.agentId, outcome: search.outcome, severity: search.severity, signal: search.signal }),
    [search.q, search.agentId, search.outcome, search.severity, search.signal],
  );
  const range = search.from && search.to ? { from: search.from, to: search.to } : undefined;
  const resultFilters = useMemo(() => ({ ...filters, from: search.from, to: search.to }), [filters, search.from, search.to]);
  const hasFilters = Object.values(resultFilters).some(Boolean);
  const rangeLabel = range
    ? Date.parse(range.to) - Date.parse(range.from) === BUCKET
      ? fmtBucket(Date.parse(range.from))
      : `${fmtDateTime(range.from)} – ${fmtDateTime(range.to)}`
    : null;

  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  return (
    <Page>
      <PageHeader title="Traces" description="Every agent run with its spans, behavioral signals and control outcomes. Filter with the query language or the quick filters." />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <QueryBar value={search.q ?? ""} onApply={(v) => setSearch({ q: clean(v) })} className="min-w-[360px] flex-1" />
        <Select
          aria-label="Filter by agent"
          value={search.agentId ?? ""}
          onChange={(e) => setSearch({ agentId: e.target.value || undefined })}
          className="w-44"
        >
          <option value="">All agents</option>
          {agents.data?.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filter by outcome"
          value={search.outcome ?? ""}
          onChange={(e) => setSearch({ outcome: (e.target.value || undefined) as (typeof OUTCOMES)[number] | undefined })}
          className="w-36"
        >
          <option value="">Any outcome</option>
          {OUTCOMES.map((o) => (
            <option key={o} value={o}>
              {o[0]!.toUpperCase() + o.slice(1)}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Minimum signal severity"
          value={search.severity ?? ""}
          onChange={(e) => setSearch({ severity: (e.target.value || undefined) as (typeof SEVERITIES)[number] | undefined })}
          className="w-40"
        >
          <option value="">Any severity</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              Severity ≥ {s}
            </option>
          ))}
        </Select>
        {search.signal ? (
          <span className="inline-flex h-8 items-center gap-1 rounded-md border border-border-strong bg-surface-2 pl-2.5 pr-1 font-mono text-[12px] text-fg-2">
            signal:{search.signal}
            <button
              type="button"
              aria-label="Remove signal filter"
              onClick={() => setSearch({ signal: undefined })}
              className="grid size-6 cursor-pointer place-items-center rounded text-fg-3 hover:bg-surface-3 hover:text-fg"
            >
              <X className="size-3.5" />
            </button>
          </span>
        ) : null}
        {rangeLabel ? (
          <span className="inline-flex h-8 items-center gap-1 rounded-md border border-border-strong bg-surface-2 pl-2.5 pr-1 text-[12px] text-fg-2">
            <CalendarRange className="size-3.5 text-fg-3" aria-hidden />
            {rangeLabel}
            <button
              type="button"
              aria-label="Remove time range filter"
              onClick={() => setSearch({ from: undefined, to: undefined })}
              className="grid size-6 cursor-pointer place-items-center rounded text-fg-3 hover:bg-surface-3 hover:text-fg"
            >
              <X className="size-3.5" />
            </button>
          </span>
        ) : null}
        {hasFilters ? (
          <Button variant="ghost" size="md" onClick={() => void navigate({ search: {}, replace: true })}>
            Clear all
          </Button>
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        <VolumeHistogram filters={filters} range={range} onRange={(r) => setSearch({ from: r?.from, to: r?.to })} />
        <TraceResults filters={resultFilters} hasFilters={hasFilters} onClear={() => void navigate({ search: {}, replace: true })} />
      </div>
    </Page>
  );
}

function TraceResults({ filters, hasFilters, onClear }: { filters: TraceFilters & { from?: string; to?: string }; hasFilters: boolean; onClear: () => void }) {
  const navigate = Route.useNavigate();
  const filterKey = JSON.stringify(filters);
  // Cursors for "Load more"; reset whenever the filters change.
  const [paging, setPaging] = useState<{ key: string; cursors: Array<string | undefined> }>({ key: filterKey, cursors: [undefined] });
  const cursors = paging.key === filterKey ? paging.cursors : [undefined];

  const pages = useQueries({ queries: cursors.map((cursor) => q.traces({ ...filters, limit: PAGE_SIZE, cursor })) });
  const first = pages[0]!;
  const last = pages[pages.length - 1]!;
  const seen = new Set<string>();
  const items: TraceSummary[] = [];
  for (const p of pages) {
    for (const t of p.data?.items ?? []) {
      if (seen.has(t.traceId)) continue;
      seen.add(t.traceId);
      items.push(t);
    }
  }

  const total = first.data?.total;
  const nextCursor = last.data && !last.isPlaceholderData ? last.data.nextCursor : null;
  const loadingMore = pages.length > 1 && last.isFetching;
  const refreshing = first.isPlaceholderData && first.isFetching;

  if (first.error) {
    return (
      <Card>
        <ErrorPanel error={first.error} reset={() => void first.refetch()} className="m-4" />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Results"
        description={
          total === undefined ? "Loading…" : `${fmtInt(total)} ${total === 1 ? "trace" : "traces"}${hasFilters ? " match" : ""} · newest first`
        }
        actions={refreshing ? <Loader2 className="size-3.5 animate-spin text-fg-3" aria-label="Updating results" /> : null}
      />
      <DataTable
        data={first.isPending ? undefined : items}
        loading={first.isPending}
        columns={traceColumns}
        dense
        stickyHeader={false}
        getRowId={(t) => t.traceId}
        onRowClick={(t) => void navigate({ to: "/traces/$traceId", params: { traceId: t.traceId } })}
        className={refreshing ? "opacity-60 transition-opacity" : "transition-opacity"}
        empty={{
          icon: <ListTree />,
          title: hasFilters ? "No traces match these filters" : "No traces yet",
          description: hasFilters ? (
            <>
              Try widening the query or{" "}
              <button type="button" className="cursor-pointer text-accent hover:underline" onClick={onClear}>
                clear all filters
              </button>
              .
            </>
          ) : (
            "Traces appear here as soon as the SDK or OTLP exporter sends spans."
          ),
        }}
      />
      {items.length ? (
        <div className="flex items-center justify-between gap-3 border-t px-4 py-2.5 text-xs text-fg-3">
          <span className="tabular">
            Showing {fmtInt(items.length)} of {fmtInt(total ?? items.length)}
          </span>
          {nextCursor ? (
            <Button
              variant="outline"
              size="sm"
              disabled={loadingMore}
              onClick={() => setPaging({ key: filterKey, cursors: [...cursors, nextCursor] })}
            >
              {loadingMore ? <Loader2 className="animate-spin" /> : null}
              Load {fmtInt(Math.min(PAGE_SIZE, (total ?? 0) - items.length))} more
            </Button>
          ) : loadingMore ? (
            <Loader2 className="size-3.5 animate-spin" aria-label="Loading more" />
          ) : (
            <span>End of results</span>
          )}
        </div>
      ) : null}
    </Card>
  );
}
